import useFocusPerson from './useFocusPerson'
import useMyHeritageHintsFlag from './useMyHeritageHintsFlag'

/**
 * Single source of truth for whether MyHeritage hints may be shown for the focus person.
 *
 * Three conditions must all hold:
 * 1. The patron is opted into the MyHeritage Lab. The flag and the `fslabs.labs` preference are
 *    registered together in CachedFlagsProvider, so this only has to read the evaluation.
 * 2. The focus person is not living. Partner-site hints are never shown for living people.
 * 3. The focus person is publicly visible, so restricted data is not sent off to a partner site.
 *
 * Extracted from ResearchHelpCard so that the person page card and the Hints tab
 * (ResearchHelpContent) share one rule and cannot drift apart.
 *
 * The lab flag is read through the cached evaluation, which hydrates from localStorage rather than
 * making this wait on `loading`. Gating on `loading` kept the section out of the first paint and
 * held back the partner request until every other section had already loaded. The tradeoff is that
 * a patron who left the lab since their last visit is treated as opted in until the flag resolves.
 * Only the lab check is cached, so the living and visibility rules above still hold, and the worst
 * case is one partner request for a person who was eligible anyway.
 */
export default function useShowMyHeritageHints(): boolean {
  const { focusPerson } = useFocusPerson()
  const { data: myHeritageFlag } = useMyHeritageHintsFlag()

  const isOptedIntoLab = !!myHeritageFlag?.isOn
  const isPersonEligible = !focusPerson?.living && !!focusPerson?.visibleToAllWhenUsingFamilySearchApps

  return isOptedIntoLab && isPersonEligible
}
